// Author: Çağrı Erdem, 2024
// Description: Utils for dB study2



////////////////
// dB-related //
////////////////

export function generateDummyMidi(numNotes = 3, deltaTime = 480) {
  const dummyMidi = {
      type: "track",
      events: []
  };

  // Add meta event for setting tempo (optional)
  dummyMidi.events.push({
      deltaTime: 0, // No time before the tempo change
      meta: true,
      type: "setTempo",
      microsecondsPerBeat: 500000 // Tempo: 120 BPM
  });

  // Generate noteOn and noteOff events
  for (let i = 0; i < numNotes; i++) {
      // Note On: channel 1, note number 0, velocity 0
      dummyMidi.events.push({
          deltaTime: deltaTime, // Time before this event
          channel: 0,
          type: "noteOn",
          noteNumber: 0, // Dummy note number
          velocity: 0    // Dummy velocity
      });

      // Note Off: channel 1, note number 0, velocity 0
      dummyMidi.events.push({
          deltaTime: deltaTime, // Time before this event
          channel: 0,
          type: "noteOff",
          noteNumber: 0, // Dummy note number
          velocity: 0    // Dummy velocity
      });
  }

  // End of track event
  dummyMidi.events.push({
      deltaTime: 0, // No time before the end of the track
      meta: true,
      type: "endOfTrack"
  });

  return dummyMidi;
}


export function generateDummyMidiArrayBuffer(numNotes = 3, deltaTime = 480) {
  // MIDI header chunk
  const headerChunk = [
      0x4D, 0x54, 0x68, 0x64, // "MThd"
      0x00, 0x00, 0x00, 0x06, // Header length (6 bytes)
      0x00, 0x01,             // Format type 1 (multitrack)
      0x00, 0x01,             // Number of tracks
      0x01, 0xE0              // Time division (480 ticks per quarter note)
  ];

  // Track events
  const trackEvents = [];

  // Tempo meta event (set to 120 BPM)
  trackEvents.push(0x00, 0xFF, 0x51, 0x03, 0x07, 0xA1, 0x20); // Tempo: 120 BPM

  // Generate noteOn and noteOff events
  for (let i = 0; i < numNotes; i++) {
      // Note On: Channel 1, Note C4 (60), Velocity 64
      trackEvents.push(deltaTime, 0x90, 0x3C, 0x40); // deltaTime, noteOn, noteNumber, velocity

      // Note Off: Channel 1, Note C4 (60), Velocity 0
      trackEvents.push(deltaTime, 0x80, 0x3C, 0x00); // deltaTime, noteOff, noteNumber, velocity
  }

  // End of Track event
  trackEvents.push(0x00, 0xFF, 0x2F, 0x00); // End of track

  // Calculate the length of the track chunk
  const trackChunkLength = trackEvents.length;

  // Create track chunk header
  const trackChunkHeader = [
      0x4D, 0x54, 0x72, 0x6B, // "MTrk"
      (trackChunkLength >> 24) & 0xFF, // Length byte 1
      (trackChunkLength >> 16) & 0xFF, // Length byte 2
      (trackChunkLength >> 8) & 0xFF,  // Length byte 3
      trackChunkLength & 0xFF           // Length byte 4
  ];

  // Combine header and track chunk into one array
  const midiData = Uint8Array.from([
      ...headerChunk,
      ...trackChunkHeader,
      ...trackEvents
  ]);

  // Return the MIDI data as an ArrayBuffer
  return midiData.buffer;
}








////////////////
// face-pose-track //
////////////////


// Object to map blend shape names to their corresponding display elements
export const blendShapeDisplays = {
    neutral: document.getElementById('neutral'),
    browDownLeft: document.getElementById('browDownLeft'),
    browDownRight: document.getElementById('browDownRight'),
    browInnerUp: document.getElementById('browInnerUp'),
    browOuterUpLeft: document.getElementById('browOuterUpLeft'),
    browOuterUpRight: document.getElementById('browOuterUpRight'),
    cheekPuff: document.getElementById('cheekPuff'),
    cheekSquintLeft: document.getElementById('cheekSquintLeft'),
    cheekSquintRight: document.getElementById('cheekSquintRight'),
    eyeBlinkLeft: document.getElementById('eyeBlinkLeft'),
    eyeBlinkRight: document.getElementById('eyeBlinkRight'),
    eyeLookDownLeft: document.getElementById('eyeLookDownLeft'),
    eyeLookDownRight: document.getElementById('eyeLookDownRight'),
    eyeLookInLeft: document.getElementById('eyeLookInLeft'),
    eyeLookInRight: document.getElementById('eyeLookInRight'),
    eyeLookOutLeft: document.getElementById('eyeLookOutLeft'),
    eyeLookOutRight: document.getElementById('eyeLookOutRight'),
    eyeLookUpLeft: document.getElementById('eyeLookUpLeft'),
    eyeLookUpRight: document.getElementById('eyeLookUpRight'),
    eyeSquintLeft: document.getElementById('eyeSquintLeft'),
    eyeSquintRight: document.getElementById('eyeSquintRight'),
    eyeWideLeft: document.getElementById('eyeWideLeft'),
    eyeWideRight: document.getElementById('eyeWideRight'),
    jawForward: document.getElementById('jawForward'),
    jawLeft: document.getElementById('jawLeft'),
    jawOpen: document.getElementById('jawOpen'),
    jawRight: document.getElementById('jawRight'),
    mouthClose: document.getElementById('mouthClose'),
    mouthDimpleLeft: document.getElementById('mouthDimpleLeft'),
    mouthDimpleRight: document.getElementById('mouthDimpleRight'),
    mouthFrownLeft: document.getElementById('mouthFrownLeft'),
    mouthFrownRight: document.getElementById('mouthFrownRight'),
    mouthFunnel: document.getElementById('mouthFunnel'),
    mouthLeft: document.getElementById('mouthLeft'),
    mouthLowerDownLeft: document.getElementById('mouthLowerDownLeft'),
    mouthLowerDownRight: document.getElementById('mouthLowerDownRight'),
    mouthPressLeft: document.getElementById('mouthPressLeft'),
    mouthPressRight: document.getElementById('mouthPressRight'),
    mouthPucker: document.getElementById('mouthPucker'),
    mouthRight: document.getElementById('mouthRight'),
    mouthRollLower: document.getElementById('mouthRollLower'),
    mouthRollUpper: document.getElementById('mouthRollUpper'),
    mouthShrugLower: document.getElementById('mouthShrugLower'),
    mouthShrugUpper: document.getElementById('mouthShrugUpper'),
    mouthSmileLeft: document.getElementById('mouthSmileLeft'),
    mouthSmileRight: document.getElementById('mouthSmileRight'),
    mouthStretchLeft: document.getElementById('mouthStretchLeft'),
    mouthStretchRight: document.getElementById('mouthStretchRight'),
    mouthUpperUpLeft: document.getElementById('mouthUpperUpLeft'),
    mouthUpperUpRight: document.getElementById('mouthUpperUpRight'),
    noseSneerLeft: document.getElementById('noseSneerLeft'),
    noseSneerRight: document.getElementById('noseSneerRight')
  };
  
  // Function to update the display for each blend shape feature
  export function updateBlendShapeDisplay(blendShapes) {
    Object.keys(blendShapeDisplays).forEach(categoryName => {
      const score = findScoreForCategory(blendShapes, categoryName);
      const displayElement = blendShapeDisplays[categoryName];
      if (displayElement) displayElement.textContent = `${categoryName}: ${score ? score.toFixed(4) : '0.0000'}`;
    });
  }
  
  
  
  export function findScoreForCategory(data, categoryName, type = 'blendShape') {
    //TODO: Simplify the function and add comments to explain the purpose of the function.
    if (type === 'blendShape') {
      // Handle blend shapes
      for (const element of data) {
        const category = element.categories.find(category => category.categoryName === categoryName);
        if (category) {
          return category.score;
        }
      }
    } else if (type === 'pose') {
      // Handle pose data based on the provided category name
      // Assuming the categoryName is the name of the landmark or related to the landmark index
      switch (categoryName) {
        case 'leftShoulder':
          return data[11]?.visibility || 0; // Assuming 11 is left shoulder
        case 'rightShoulder':
          return data[12]?.visibility || 0; // Assuming 12 is right shoulder
        case 'leftElbow':
          return data[13]?.visibility || 0; // Assuming 13 is left elbow
        case 'rightElbow':
          return data[14]?.visibility || 0; // Assuming 14 is right elbow
        case 'leftHand':
          return data[15]?.visibility || 0; // Assuming 15 is left wrist
        case 'rightHand':
          return data[16]?.visibility || 0; // Assuming 16 is right wrist
        // Add more cases if needed
        default:
          return 0;
      }
    }
    return 0;
  }
  
  